import * as React from 'react';
import { Pagination, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseCircleOutlined } from '@ant-design/icons';
import Search from 'antd/es/input/Search';

interface IEndpoint {
    method: string;
    path: string;
    scope?: string;
    userScope?: string;
}

interface IProps {
    endpoints?: IEndpoint[] | null;
    loading?: boolean;
}

const ApiAccessEndpointsTab: React.FC<IProps> = ({ endpoints, loading }) => {
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [searchValue, setSearchValue] = React.useState('');
    const tableHeaderCellClass =
        '!bg-white !px-4 !py-3 !text-sm !font-semibold !text-gray-900 !border-b !border-gray-100';
    const tableBodyCellClass = '!px-4 !py-3.5 !align-middle !border-b !border-gray-50';

    const filteredEndpoints = React.useMemo(() => {
        const data = endpoints || [];
        if (!searchValue) {
            return data;
        }
        const normalized = searchValue.toLowerCase();
        return data.filter((endpoint) => {
            const methodMatch = endpoint.method?.toLowerCase().includes(normalized);
            const pathMatch = endpoint.path?.toLowerCase().includes(normalized);
            const scopeMatch = endpoint.scope?.toLowerCase().includes(normalized);
            const userScopeMatch = endpoint.userScope?.toLowerCase().includes(normalized);
            return methodMatch || pathMatch || scopeMatch || userScopeMatch;
        });
    }, [endpoints, searchValue]);
    const paginatedEndpoints = React.useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        return filteredEndpoints.slice(startIndex, startIndex + pageSize);
    }, [filteredEndpoints, page, pageSize]);

    const columns: ColumnsType<IEndpoint> = [
        {
            title: 'API Endpoints',
            dataIndex: 'path',
            onHeaderCell: () => ({ className: tableHeaderCellClass }),
            onCell: () => ({ className: tableBodyCellClass }),
            render: (_value, record) => (
                <span className="font-medium text-gray-900">
                    <span className="mr-2 inline-flex min-w-[72px] justify-center rounded bg-gray-100 px-2 py-1 text-xs font-semibold tracking-wide text-gray-700">
                        {record.method}
                    </span>
                    {record.path}
                </span>
            ),
        },
        {
            title: 'Scope',
            dataIndex: 'scope',
            align: 'right',
            width: 220,
            onHeaderCell: () => ({ className: `${tableHeaderCellClass} !text-right` }),
            onCell: () => ({ className: `${tableBodyCellClass} !text-right` }),
            render: (_value, record) => record.scope || record.userScope || '',
        },
    ];

    const dataSource = paginatedEndpoints.map((endpoint, index) => ({
        key: `${endpoint.method}-${endpoint.path}-${index}`,
        ...endpoint,
    }));

    return (
        <>
            <Search
                className="mb-4"
                placeholder="Search endpoints"
                value={searchValue}
                onChange={(event) => {
                    setSearchValue(event.target.value);
                    setPage(1);
                }}
                suffix={
                    <CloseCircleOutlined
                        className={`cursor-pointer text-gray-400 hover:text-gray-600 ${searchValue.length > 0 ? 'opacity-100' : 'opacity-0'}`}
                        onClick={() => {
                            setSearchValue('');
                            setPage(1);
                        }}
                    />
                }
            />
            <Table
                dataSource={dataSource}
                columns={columns}
                loading={loading}
                className="w-full pb-4 rounded-lg border border-gray-200 bg-white"
                rowClassName={() => 'hover:[&>td]:!bg-gray-50'}
                pagination={false}
            />
            <div className="flex justify-end mt-4">
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={filteredEndpoints.length}
                    onChange={(nextPage, nextPageSize) => {
                        setPage(nextPage);
                        setPageSize(nextPageSize);
                    }}
                    showSizeChanger
                    pageSizeOptions={['10', '20', '50', '100']}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                />
            </div>
        </>
    );
};

export default ApiAccessEndpointsTab;
